import React, { useState, useEffect, useCallback, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import "./MenuBar.css"; // Import the CSS file for styling
import { RollupButton } from "../Components/RollupButton";
import { IdeationButton } from "./IdeationButton";
import { MS365Button } from "./MS365Button";
import { AIButton } from "./AIButton";
import { DetailsButton } from "./DetailsButton";
import TourGuideButton from "./TourGuideButton";
import { FocusFilterButton } from "./FocusFilterButton";
import { IncomingDataButton } from "./IncomingDataButton";
import { Menubar } from "primereact/menubar";
// import "primereact/resources/themes/vela-orange/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { Slider } from "primereact/slider";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import type { MenuItem } from "primereact/menuitem";
import { Badge } from "primereact/badge";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";

// Extend MenuItem to include shortcut property
interface ExtendedMenuItem extends MenuItem {
  shortcut?: string;
  items?: ExtendedMenuItem[];
}
import { IoClose, IoSearchOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { CgUndo, CgRedo } from "react-icons/cg";
import { GoArrowLeft, GoArrowRight } from "react-icons/go";
import { BsMicrosoft } from "react-icons/bs";
import { TbNumbers, TbCalculator } from "react-icons/tb";
import { useStore } from "../App/store"; // Import the store
import {
  handleExportToExcel,
  handleExportToWord,
  handleExportToPDF,
} from "../Helpers/ExportData";
import { handleImportMindManager } from "../Helpers/ImportData";
import { ResponseType, viewTypes, values } from "../types/types";
import { insightsCollection } from "../insightsCollection";
import { ToggleButton } from "primereact/togglebutton";
import { loadFlowProps, saveFlowProps } from "../storage/storageProxy";
import { getCalculatedValue } from "../Helpers/Calculations";
import {
  addCollaborator,
  removeCollaborator,
  loadCollaborators,
  getUserPlan,
  addFeedback,
  getUnlockedFeatures,
} from "../storage/storageProxy"; // Import Firestore functions
import { firebaseFunctionsUrl } from "../Helpers/FirebaseEnvironments";
import axios from "axios";
import HelpModal from "./HelpModal"; // Import the HelpModal component
import CustomAlert from "./CustomAlert";
import FeedbackForm from "./FeedbackForm";
import AttentionModal from "../Components/AttentionModal";
import ProModal from "./ProModal"; // Import the ProModal component
import { generateAiMap } from "../Helpers/ApiChatGpt";
import { generatePubMedMap } from "../Helpers/ApiPubMed";
import CustomPropertiesModal from "./CustomPropertiesModal";
import { CustomProperty } from "../types/types";
// import type { MenuItem } from "primereact/menuitem";
import type { MS365Document, MS365DocumentFilter } from "../types/types";

interface MenuBarProps {
  //onLoad: () => void;
  onSave: () => void;
  onImport: (template: any) => void;
  onImportWord: () => void;
  onExport: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onAutoCircle: () => void;
  onAutoTree: (direction: string) => void;
  onFeatures: () => void;
  onShare: () => void;
  onFocus: (value: string) => void;
  focusValue: string;
  onToggleSnapToGrid: () => void;
  snapToGrid: boolean;
  onToggleShowMiniMap: () => void;
  showMiniMap: boolean;
  onToggleAIInput: () => void;
  flowId: string | undefined;
  onSetNodes: (nodes: any[]) => void;
  onSetEdges: (edges: any[]) => void;
  onExportToPng: () => void;
  onExportToClipboard: () => void;
  captureScreenshot: () => Promise<string | null>;
  onFitView: () => void;
  onFitMapView: (includeHiddenNodes: boolean) => void; // Add the new prop
  focusOnNode: (nodeId: string) => void;
  onShowTourGuide?: () => void;
  setShowAiModal: (showAiModal: boolean) => void;
  setShowPubMedModal: (showPubMedModal: boolean) => void;
  setShowFocusAiModal: (showFocusAiModal: boolean) => void;
  setShowBonsaiModal: (showBonsaiModal: boolean) => void;
  setBrainstormVisible: (visible: boolean) => void;
  guideVisible: boolean;
  setGuideVisible: (visible: boolean) => void;
  //handleSideBarToggle: () => void;
  handleUserSettings: () => void;
  setShowCvrModal: (showCvrModal: boolean) => void;
  ideationMode: boolean;
  setIdeationMode: (ideationMode: boolean) => void;
  ms365Mode: boolean;
  setMS365Mode: (ms365Mode: boolean) => void;
  aiMode: boolean;
  setAIMode: (aiMode: boolean) => void;
  detailsMode: boolean;
  setDetailsMode: (detailsMode: boolean) => void;
  onSaveAsTemplate: () => void;
  handleMapData: (data: any) => void;
  mapTemplate?: string;
  msDocumentFiltering: (timelineEventFiltering: MS365DocumentFilter) => void;
  setShowTips: (tabIndex?: number) => void;
}

const MenuBar: React.FC<MenuBarProps> = ({
  //onLoad,
  onSave,
  onImport,
  onImportWord,
  onExport,
  onUndo,
  onRedo,
  onAutoCircle,
  onAutoTree,
  onFeatures,
  onShare,
  onFocus,
  focusValue,
  onToggleSnapToGrid,
  snapToGrid,
  onToggleShowMiniMap,
  showMiniMap,
  onToggleAIInput,
  flowId,
  onSetNodes,
  onSetEdges,
  onExportToPng, // Add onExportToPng to props
  onExportToClipboard, // Add onExportToClipboard to props
  captureScreenshot, // Add captureScreenshot to props
  onFitView,
  onFitMapView, // Destructure the new prop
  focusOnNode,
  setShowAiModal,
  setShowPubMedModal,
  setShowFocusAiModal,
  setShowBonsaiModal,
  setBrainstormVisible,
  guideVisible,
  setGuideVisible,
  //handleSideBarToggle,
  handleUserSettings,
  setShowCvrModal,
  ideationMode,
  setIdeationMode,
  ms365Mode,
  setMS365Mode,
  aiMode,
  setAIMode,
  detailsMode,
  setDetailsMode,
  onShowTourGuide,
  onSaveAsTemplate,
  handleMapData,
  mapTemplate,
  msDocumentFiltering,
  setShowTips,
}) => {
  const [rollup, setRollup] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [selectedCollaborator, setSelectedCollaborator] = useState<
    string | null
  >(null);
  const [email, setEmail] = useState("");
  const [isAddingCollaborator, setIsAddingCollaborator] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedHelpTopic, setSelectedHelpTopic] = useState<string | null>(
    null
  );
  const isDirty = useStore.getState().isDirty;
  const [showProModal, setShowProModal] = useState(false);

  const currentView = useStore.getState().currentView;
  const setCurrentView = useStore.getState().setCurrentView;

  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [topic, setTopic] = useState("");
  const [depth, setDepth] = useState("5");
  const [source, setSource] = useState("AI");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string[]>([
    "Bringing",
    "your topic",
    "to life...",
  ]);
  const [hasSearchHits, setHasSearchHits] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [includeNotes, setIncludeNotes] = useState(false);
  const [lastSearchIndex, setLastSearchIndex] = useState(-1);
  const [isFeatureUnlocked, setIsFeatureUnlocked] = useState<string[]>([]);
  const presentationMode = useRef("off");
  const presentationStep = useRef(1);
  const isPresentationMode = useStore.getState().isPresentationMode;
  const [presentationButtonRunning, setPresentationButtonRunning] =
    useState(false);
  const [showCustomPropertiesModal, setShowCustomPropertiesModal] =
    useState(false);
  const [initialCustomProperties, setInitialCustomProperties] = useState<
    CustomProperty[]
  >([]);
  const [menuSourceOptions, setMenuSourceOptions] = useState<string[]>([]);

  const [deleteUnselectedMS365OnFilter, setDeleteUnselectedMS365OnFilter] =
    useState(true);
  const [useCopilot, setUseCopilot] = useState(false);
  const [ms365FromDate, setMS365FromDate] = useState<Date | null>(null);
  const [ms365ToDate, setMS365ToDate] = useState<Date | null>(null);
  const [maxResults, setMaxResults] = useState<number | null>(10);
  const [selectedFileTypes, setSelectedFileTypes] = useState([
    "email",
    "event",
    "chat",
    "docx",
    "xlsx",
    "pptx",
    "pdf",
    "onenote",
    "teams",
    "sharepoint",
    "attachment",
    "contact",
    "todo",
    "notes",
    "journal",
  ]);
  const [ms365OutputMode, setMS365OutputMode] = useState<string>("picklist");
  const [showMS365PickListDialog, setShowMS365PickListDialog] = useState(false);
  const [ms365PickListData, setMS365PickListData] = useState<MS365Document[]>(
    []
  );
  const [ms365PickListSearchText, setMS365PickListSearchText] = useState("");
  const [addedMS365Items, setAddedMS365Items] = useState<Set<string>>(
    new Set()
  );

  // Focus Filter state
  const [showFocusFilterDialog, setShowFocusFilterDialog] = useState(false);
  const [showIncomingDataDialog, setShowIncomingDataDialog] = useState(false);
  const [incomingDataCount, setIncomingDataCount] = useState(0);
  const [showValues, setShowValues] = useState(false);
  const [doRollup, setDoRollup] = useState(false);
  const focusFilterStatuses = useStore((state) => state.focusFilterStatuses);
  const setFocusFilterStatuses = useStore(
    (state) => state.setFocusFilterStatuses
  );
  const [tempFilterStatuses, setTempFilterStatuses] = useState<string[]>([]);

  const taskStatusOptions = [
    // { label: "Select All", value: "all" },
    { label: "None", value: "none" },
    { label: "To Do", value: "Open" },
    { label: "In Progress", value: "InProgress" },
    { label: "Testing", value: "Testing" },
    { label: "Impediment", value: "Impediment" },
    { label: "Done", value: "Done" },
  ];

  useEffect(() => {
    const fetchUserPlan = async () => {
      const plan = await getUserPlan(
        auth.currentUser,
        useStore.getState().useLocalStorage
      );
      setUserPlan(plan ? plan : "free");
    };
    fetchUserPlan();
  }, []);

  useEffect(() => {
    const inputField = document.querySelector(".topic-input");
    if (inputField) {
      inputField.setAttribute("spellcheck", "false");
    }
    const doNotShowAgain = localStorage.getItem("doNotShowAttentionModal");
    if (!doNotShowAgain) {
      setShowAttentionModal(true);
    }
  }, []);

  useEffect(() => {
    const checkFeatureUnlock = async () => {
      const unlockedFeatures = await getUnlockedFeatures(
        useStore.getState().useLocalStorage
      );
      setIsFeatureUnlocked(unlockedFeatures);
    };
    checkFeatureUnlock();
  }, []);

  useEffect(() => {
    setMenuSourceOptions(useStore.getState().sourceOptions);
  }, [useStore.getState().sourceOptions]);

  useEffect(() => {
    const handleMS365DataReady = (event: CustomEvent) => {
      setMS365PickListData(event.detail);
      setMS365Mode(false);
      setIdeationMode(false); // Deactivate ideation mode when opening Pick List
      setAddedMS365Items(new Set()); // Reset added items tracker
      setShowMS365PickListDialog(true);
    };

    window.addEventListener(
      "ms365DataReady",
      handleMS365DataReady as EventListener
    );

    return () => {
      window.removeEventListener(
        "ms365DataReady",
        handleMS365DataReady as EventListener
      );
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        // Check if notes modal is open - if so, let it handle the save
        const notesModal = document.querySelector(".notes-modal-dialog");
        if (notesModal) {
          return; // Notes modal will handle Ctrl+S
        }
        event.preventDefault();
        onSave();
      } else if (event.key === "F5") {
        event.preventDefault();
        doPresent();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onSave]);

  const changeMS365FromDate = (date: Date) => {
    if (ms365ToDate && date && date.getTime() > ms365ToDate.getTime()) {
      showAlert(
        "Warning",
        "From date cannot be later than To date. Please adjust the dates."
      );
      return false;
    }
    setMS365FromDate(date);
  };

  const changeMS365ToDate = (date: Date) => {
    if (ms365FromDate && date && date.getTime() < ms365FromDate.getTime()) {
      showAlert(
        "Warning",
        "To date cannot be earlier than From date. Please adjust the dates."
      );
      return false;
    }
    setMS365ToDate(date);
  };

  const handleCloseAttentionModal = () => {
    setShowAttentionModal(false);
  };

  const handleDoNotShowAttentionModalAgain = () => {
    localStorage.setItem("doNotShowAttentionModal", "true");
    setShowAttentionModal(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = "/signin"; // Redirect to sign-in page after sign-out
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const navigate = useNavigate();

  const handleBackClick = () => {
    if (isDirty) {
      if (
        window.confirm("You have unsaved changes. Do you want to continue?")
      ) {
        navigate("/");
      }
    } else {
      navigate("/");
    }
  };

  const handleShare = async () => {
    if (!flowId) return;
    setShowShareModal(true);
    const loadedCollaborators = await loadCollaborators(
      flowId,
      useStore.getState().useLocalStorage
    );
    setCollaborators(loadedCollaborators);
  };

  // Shortcut handlers
  const handleLayoutChildren = () => {
    const selectedNodeId = useStore.getState().selectedNodeId;
    if (selectedNodeId) {
      const { redistributeChildNodes } = useStore.getState();
      redistributeChildNodes(selectedNodeId);
    }
  };

  const handleLayoutAllBranches = () => {
    const selectedNodeId = useStore.getState().selectedNodeId;
    if (selectedNodeId) {
      const { redistributeAllChildNodes } = useStore.getState();
      redistributeAllChildNodes(selectedNodeId);
    }
  };

  const handleLayoutRadial = () => {
    const selectedNodeId = useStore.getState().selectedNodeId;
    if (selectedNodeId) {
      const { redistributeAllChildNodesRadial } = useStore.getState();
      redistributeAllChildNodesRadial(selectedNodeId);
    }
  };

  const handleIncreaseSpacing = () => {
    const selectedNodeId = useStore.getState().selectedNodeId;
    const { increaseChildNodeDistance, redistributeChildNodes } =
      useStore.getState();
    increaseChildNodeDistance();
    if (selectedNodeId) {
      redistributeChildNodes(selectedNodeId);
    }
  };

  const handleDecreaseSpacing = () => {
    const selectedNodeId = useStore.getState().selectedNodeId;
    const { decreaseChildNodeDistance, redistributeChildNodes } =
      useStore.getState();
    decreaseChildNodeDistance();
    if (selectedNodeId) {
      redistributeChildNodes(selectedNodeId);
    }
  };

  const handleIncreaseGlobalSpacing = () => {
    const { increaseGlobalNodeDistance } = useStore.getState();
    increaseGlobalNodeDistance();
  };

  const handleDecreaseGlobalSpacing = () => {
    const { decreaseGlobalNodeDistance } = useStore.getState();
    decreaseGlobalNodeDistance();
  };

  const doPresent = () => {
    if (presentationMode.current === "on") {
      setPresentationModeOff();
    } else {
      setPresentationModeOn();
    }
    const presentButton = document.querySelector("button.presentButton");
    if (presentButton) {
      (presentButton as HTMLButtonElement).blur();
    }
  };

  useEffect(() => {
    if (presentationMode.current === "on") {
      window.addEventListener("keydown", handleKeyPressDuringPresentation);
      // window.addEventListener("click", handleKeyPressDuringPresentation);
    } else {
      window.removeEventListener("keydown", handleKeyPressDuringPresentation);
      // window.removeEventListener("click", handleKeyPressDuringPresentation);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyPressDuringPresentation);
      // window.removeEventListener("click", handleKeyPressDuringPresentation);
    };
  }, [presentationMode.current]);

  function setPresentationModeOff() {
    useStore.getState().setIsPresentationMode(false);
    presentationMode.current = "off";
    presentationStep.current = 1;
    setPresentationButtonRunning(false);
    showAlert("Presentation", "The presentation has ended.");
  }

  function setPresentationModeOn() {
    useStore.getState().setIsPresentationMode(true);
    presentationMode.current = "on";
    presentationStep.current = 1;
    setPresentationButtonRunning(true);
    useStore.getState().hideChildNodes("root");
    onFitMapView(false);
  }

  const handleKeyPressDuringPresentation = useCallback(
    (event: KeyboardEvent | MouseEvent) => {
      const nodes = useStore.getState().nodes;

      const extractNumber = (label: string): number => {
        const match = label;
        return match ? parseInt(match, 10) : 99999;
      };

      if (
        (event instanceof KeyboardEvent && event.code === "ArrowRight") ||
        event instanceof MouseEvent
      ) {
        const revealNextNode = (): boolean => {
          const hiddenNodes = nodes
            .filter((node) => node.hidden)
            .sort(
              (a, b) =>
                extractNumber(a.data.presentationOrder) -
                extractNumber(b.data.presentationOrder)
            );

          if (hiddenNodes.length > 0) {
            const nextNode = hiddenNodes[0];
            useStore.getState().showChildNode(nextNode.id);
            setTimeout(() => {
              const inputField = document.querySelector(
                `input[node-identifier="${nextNode.id}"]`
              );
              if (inputField) {
                (inputField as HTMLInputElement).blur();
              }
            }, 100);
            onFitMapView(false);
            return true;
          }
          return false;
        };

        if (!revealNextNode()) {
          setPresentationModeOff();
        }
      } else if (event instanceof KeyboardEvent && event.code === "ArrowLeft") {
        const hidePreviousNode = (): boolean => {
          const visibleNodes = nodes
            .filter((node) => !node.hidden && node.type !== "center")
            .sort(
              (a, b) =>
                extractNumber(a.data.presentationOrder) -
                extractNumber(b.data.presentationOrder)
            );

          if (visibleNodes.length > 0) {
            const lastNode = visibleNodes[visibleNodes.length - 1];
            useStore.getState().hideChildNode(lastNode.id);
            setTimeout(() => {
              const inputField = document.querySelector(
                `input[node-identifier="${lastNode.id}"]`
              );
              if (inputField) {
                (inputField as HTMLInputElement).blur();
              }
            }, 100);
            onFitMapView(false);
            return true;
          }
          return false;
        };

        hidePreviousNode();
      }
    },
    [onFitMapView]
  );

  const handleAddCollaborator = async () => {
    if (email && flowId) {
      if (collaborators.includes(email)) {
        alert("Collaborator already added.");
        return;
      }
      if (auth.currentUser && auth.currentUser.email === email) {
        alert("You are the owner and already have access.");
        return;
      }
      if (auth.currentUser && auth.currentUser.email) {
        setIsAddingCollaborator(true);
        try {
          // Add collaborator to database
          await addCollaborator(
            flowId,
            auth.currentUser.email,
            email,
            useStore.getState().useLocalStorage
          );

          // Send invitation email
          try {
            const currentNodes = useStore.getState().nodes;
            const rootNode = currentNodes.find(
              (node) => node.data.label === "root" || node.id === "root"
            );
            const mapTitle = rootNode?.data.label || "Untitled Map";

            // Determine if we're on production or preview
            const domain =
              window.location.hostname === "focusmap.app"
                ? "production"
                : "preview";

            await axios.post(
              firebaseFunctionsUrl("send-collaboration-invite"),
              {
                email: email,
                ownerEmail: auth.currentUser.email,
                mapId: flowId,
                mapTitle: mapTitle,
                domain: domain,
              }
            );

            console.log(`Invitation email sent to ${email}`);
          } catch (error) {
            console.error("Failed to send invitation email:", error);
            // Don't fail the whole operation if email fails
            alert(
              "Collaborator added, but invitation email could not be sent."
            );
          }

          setCollaborators([...collaborators, email]);
          setEmail("");
        } catch (error) {
          console.error("Error adding collaborator:", error);
          alert("Failed to add collaborator. Please try again.");
        } finally {
          setIsAddingCollaborator(false);
        }
      } else {
        console.error("Current user is not authenticated.");
      }
    }
  };

  const handleRemoveCollaborator = async () => {
    if (selectedCollaborator && flowId) {
      await removeCollaborator(
        flowId,
        selectedCollaborator,
        useStore.getState().useLocalStorage
      );
      setCollaborators(
        collaborators.filter(
          (collaborator) => collaborator !== selectedCollaborator
        )
      );
      setSelectedCollaborator(null);
    } else {
      alert("Please select a collaborator to remove.");
    }
  };

  const handleHelp = () => {
    setShowTips();
  };

  const handlePro = () => {
    setShowProModal(true);
  };

  const handleHelpTopicClick = (topic: string) => {
    setSelectedHelpTopic(topic);
  };

  const handleCloseHelpModal = () => {
    setShowHelpModal(false);
  };

  const handleCloseProModal = () => {
    setShowProModal(false);
  };

  const handleCloseVideoModal = () => {
    setSelectedHelpTopic(null);
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
  };

  const handleFeedbackSubmit = async () => {
    const user = auth.currentUser;
    if (user && feedback) {
      if (user.email) {
        await addFeedback(
          user.email,
          feedback,
          useStore.getState().useLocalStorage
        );
      } else {
        showAlert("Error", "User email is not available.");
      }
      setShowFeedbackModal(false);
      setFeedback("");
      showAlert("Feedback Received", "Thank you for helping us improve.");
    } else {
      showAlert("Notice", "Please enter your feedback before submitting.");
    }
  };

  useEffect(() => {
    if (isFeatureUnlocked.includes("ai")) {
      const doNotShowAgain = localStorage.getItem("doNotShowAttentionModal");
      if (!doNotShowAgain) {
        setShowAttentionModal(true);
      }
    }
  }, []);

  // Apply focus filter when nodes change or filter changes
  useEffect(() => {
    applyFocusFilter();
  }, [focusFilterStatuses]);

  const handleCloseModal = () => {
    setShowAttentionModal(false);
  };

  const handleDoNotShowAgain = () => {
    localStorage.setItem("doNotShowAttentionModal", "true");
    setShowAttentionModal(false);
  };

  // Focus Filter handlers
  const handleFocusFilterChange = (status: string) => {
    let newStatuses = [...tempFilterStatuses];

    if (status === "all") {
      if (newStatuses.includes("all")) {
        newStatuses = [];
      } else {
        newStatuses = [
          // "all",
          "none",
          "Open",
          "InProgress",
          "Testing",
          "Impediment",
          "Done",
        ];
      }
    } else {
      if (newStatuses.includes(status)) {
        newStatuses = newStatuses.filter((s) => s !== status && s !== "all");
      } else {
        newStatuses.push(status);
        // Check if all are selected
        if (
          ["none", "Open", "InProgress", "Testing", "Impediment", "Done"].every(
            (s) => newStatuses.includes(s)
          )
        ) {
          newStatuses.push("all");
        }
      }
    }

    setTempFilterStatuses(newStatuses);
  };

  const handleApplyFocusFilter = () => {
    setFocusFilterStatuses(tempFilterStatuses);
    setShowFocusFilterDialog(false);
  };

  const applyFocusFilter = () => {
    const nodes = useStore.getState().nodes;
    const edges = useStore.getState().edges;

    if (
      focusFilterStatuses.length === 0 ||
      focusFilterStatuses.includes("all")
    ) {
      // No filter or all selected - enable everything
      nodes.forEach((node) => {
        node.selectable = true;
        node.draggable = true;
        node.connectable = true;
        // Restore original backgroundColor
        if ((node.data as any).originalBackgroundColor) {
          node.data.backgroundColor = (
            node.data as any
          ).originalBackgroundColor;
          delete (node.data as any).originalBackgroundColor;
        }
        if (node.style) {
          const {
            opacity,
            backgroundColor,
            padding,
            pointerEvents,
            border,
            ...restStyle
          } = node.style;
          node.style = {
            ...restStyle,
            backgroundColor: node.data.backgroundColor,
          };
        }
        // Remove disabled state flag
        delete (node.data as any).isDisabledByFilter;
      });

      edges.forEach((edge) => {
        // Restore default edge styling
        if (edge.style) {
          const { stroke, strokeWidth, opacity, ...restStyle } = edge.style;
          edge.style = {
            ...restStyle,
            stroke: "rgb(18, 102, 86)",
            strokeWidth: 1,
            opacity: 1,
          };
        } else {
          edge.style = {
            stroke: "rgb(18, 102, 86)",
            strokeWidth: 1,
            opacity: 1,
          };
        }
      });
    } else {
      // Apply filter
      const disabledNodeIds = new Set<string>();

      nodes.forEach((node) => {
        // Skip FocusItem nodes (type === 'center') - they should never be disabled by filter
        if (node.type === "center") {
          node.selectable = true;
          node.draggable = true;
          node.connectable = true;
          // Restore original backgroundColor if saved
          if ((node.data as any).originalBackgroundColor) {
            node.data.backgroundColor = (
              node.data as any
            ).originalBackgroundColor;
            delete (node.data as any).originalBackgroundColor;
          }
          // Remove any filter-related styles
          if (node.style) {
            const {
              opacity,
              backgroundColor,
              padding,
              pointerEvents,
              border,
              ...restStyle
            } = node.style;
            node.style = {
              ...restStyle,
              backgroundColor: node.data.backgroundColor,
            };
          }
          // Remove disabled state flag
          delete (node.data as any).isDisabledByFilter;
          return;
        }

        const taskStatus = node.data.task?.status || "";
        // Treat empty string and "none" as no task (none status)
        // Treat "none" from tasks as "Open" (To Do)
        let normalizedStatus =
          taskStatus === "" && !node.data.task?.title
            ? "none"
            : taskStatus === "" || taskStatus === "none"
            ? "Open"
            : taskStatus;

        if (focusFilterStatuses.includes(normalizedStatus)) {
          // Enable node
          node.selectable = true;
          node.draggable = true;
          node.connectable = true;
          // Restore original backgroundColor
          if ((node.data as any).originalBackgroundColor) {
            node.data.backgroundColor = (
              node.data as any
            ).originalBackgroundColor;
            delete (node.data as any).originalBackgroundColor;
          }
          if (node.style) {
            const {
              opacity,
              backgroundColor,
              padding,
              pointerEvents,
              border,
              ...restStyle
            } = node.style;
            node.style = {
              ...restStyle,
              backgroundColor: node.data.backgroundColor,
            };
          }
          // Remove disabled state flag
          delete (node.data as any).isDisabledByFilter;
        } else {
          // Disable node
          node.selectable = false;
          node.draggable = false;
          node.connectable = false;
          node.style = {
            ...node.style,
            backgroundColor: "rgb(255, 255, 255)",
            border: "1px solid #d0d0d0",
            pointerEvents: "none",
          };
          // Store original backgroundColor ONLY if not already stored
          if (!(node.data as any).originalBackgroundColor) {
            (node.data as any).originalBackgroundColor =
              node.data.backgroundColor;
          }
          node.data.backgroundColor = "rgb(255, 255, 255)";
          // Store disabled state in node data
          (node.data as any).isDisabledByFilter = true;
          disabledNodeIds.add(node.id);
        }
      });

      // Dim edges where source node is disabled
      edges.forEach((edge) => {
        if (disabledNodeIds.has(edge.source)) {
          edge.style = {
            ...edge.style,
            stroke: "#d0d0d0",
            strokeWidth: 2,
            opacity: 0.3,
          };
        } else {
          // Re-enable edge - restore default green color
          if (edge.style) {
            const { stroke, strokeWidth, opacity, ...restStyle } = edge.style;
            edge.style = {
              ...restStyle,
              stroke: "rgb(18, 102, 86)",
              strokeWidth: 1,
              opacity: 1,
            };
          } else {
            edge.style = {
              stroke: "rgb(18, 102, 86)",
              strokeWidth: 1,
              opacity: 1,
            };
          }
        }
      });
    }

    // Force complete re-render by creating new node objects with deep copies
    const updatedNodes = nodes.map((node) => ({
      ...node,
      style: { ...node.style },
      data: { ...node.data },
    }));
    const updatedEdges = edges.map((edge) => ({
      ...edge,
      type: edge.type, // Preserve edge type to ensure edge components re-render
      style: edge.style ? { ...edge.style } : undefined,
    }));

    useStore.getState().setNodes(updatedNodes);
    useStore.getState().setEdges(updatedEdges);
    setShowFocusFilterDialog(false);
  };

  const clearFocusFilter = () => {
    setTempFilterStatuses([]);
    setFocusFilterStatuses([]);
    setShowFocusFilterDialog(false);
  };

  const handleSearch = () => {
    setShowSearchForm(!showSearchForm);
    setLastSearchIndex(-1);
    if (!showSearchForm) {
      setTimeout(() => {
        const searchInput = document.getElementById("search-text");
        if (searchInput) {
          searchInput.focus();
        }
      }, 0);
    } else {
      setSearchText("");
    }
  };

  const handleSearchSubmit = () => {
    if (!searchText) {
      showAlert("Search", "Please enter a search term");
      return;
    }

    //if (mapTemplate && mapTemplate === "microsoft365" && ms365Mode) {
    if (ms365Mode) {
      // Close dialog and show spinner
      setMS365Mode(false);

      //setIdeationMode(true);
      msDocumentFiltering({
        searchText,
        driveType: [],
        status: "",
        dateRange: {
          start: ms365FromDate ? ms365FromDate.toISOString() : null,
          end: ms365ToDate ? ms365ToDate.toISOString() : null,
        },
        types: selectedFileTypes as MS365Document["type"][],
        maxResults: maxResults,
        useCopilot: useCopilot,
        outputMode: ms365OutputMode,
      });
    } else {
      const nodes = useStore.getState().nodes;
      setHasSearchHits(false);
      let found = false;

      nodes.forEach((node) => {
        const labelMatch = node.data.label
          .toLowerCase()
          .includes(searchText.toLowerCase());

        const notesMatch =
          node.data.notes &&
          node.data.notes.toLowerCase().includes(searchText.toLowerCase());

        if (labelMatch || notesMatch) {
          node.selected = true;
          setHasSearchHits(true);
          found = true;
        } else {
          node.selected = false;
        }
      });

      useStore.getState().setNodes([...nodes]);

      if (!found) {
        showAlert("Search result", "No matches found.");
      }
    }
  };

  const getInsightsCollection = () => {
    return menuSourceOptions.map((sourceOption: string) => {
      const insight = insightsCollection.find(
        (insight) =>
          insight.data.title.toLowerCase() === sourceOption.toLowerCase()
      );
      const title = insight ? insight.data.title : sourceOption;
      const key = insight ? insight.data.key : sourceOption.toLowerCase();
      return {
        label: title,
        icon: insight?.icon || "pi pi-lightbulb",
        disabled: userPlan?.includes("free") && key !== "brainstorm",
        template: itemRenderer,
        command: () => {
          if (key === "chatgpt") {
            setShowAiModal(true);
          } else if (key === "focusai") {
            setShowFocusAiModal(true);
          } else if (key === "pubmed") {
            setShowPubMedModal(true);
          } else if (key === "cvr") {
            setShowCvrModal(true);
          } else if (key === "bonsai") {
            setShowBonsaiModal(true);
          } else if (key === "brainstorm") {
            setBrainstormVisible(true);
          }
        },
      };
    });
  };

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setLastSearchIndex(-1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleProFeedback = async (feedbackText: string) => {
    const user = auth.currentUser;
    if (user && user.email) {
      await addFeedback(
        user.email,
        "ProFeatureFeedback: " + feedbackText,
        useStore.getState().useLocalStorage
      );
    } else {
      showAlert("Error", "User email is not available.");
    }
  };

  const handleProButtonClick = async (buttonText: string) => {
    await handleProFeedback(buttonText);
    if (buttonText === "Maybe - show me the Plans") {
      window.open("https://www.focusmap.online/subscriptions", "_blank");
    }
    setShowProModal(false);
  };

  const createMapBySource = async () => {
    switch (source) {
      case "AI":
        generateAiMap(
          topic,
          parseInt(depth, 10),
          setLoading,
          [],
          [],
          onSetNodes,
          onSetEdges,
          showAlert,
          { x: 0, y: 0 }
        );
        break;
      case "PubMed":
        generatePubMedMap(topic, setLoading, onSetNodes, onSetEdges, showAlert);
        break;
      default:
        alert("Please select a source of knowledge.");
        break;
    }
  };

  const handlePresentationOrderMode = () => {
    if (focusValue === "presentation") {
      onFocus("all");
    } else {
      onFocus("presentation");
    }
  };

  const handlePrioritiesMode = () => {
    if (focusValue === "priorities") {
      onFocus("all");
    } else {
      onFocus("priorities");
    }
  };

  // const handleValuesMode = () => {
  //   if (focusValue === "rollup") {
  //     onFocus("all");
  //   } else {
  //     onFocus("rollup");
  //   }
  // };

  const navigateSelected = (direction: "back" | "forward") => {
    const nodes = useStore.getState().nodes;
    const selectedNodes = nodes.filter((node) => node.selected);

    if (selectedNodes.length === 0) {
      showAlert("Search result", "No matches found.");
      return;
    }

    let newIndex = lastSearchIndex;

    if (direction === "forward") {
      newIndex = (lastSearchIndex + 1) % selectedNodes.length;
    } else if (direction === "back") {
      newIndex =
        (lastSearchIndex - 1 + selectedNodes.length) % selectedNodes.length;
    }

    const nextNode = selectedNodes[newIndex];
    useStore.getState().setSelectedNodeId(nextNode.id);
    setLastSearchIndex(newIndex);

    // Call focusOnNode method
    focusOnNode(nextNode.id);
  };

  const handleLoadCustomProperties = async () => {
    if (!flowId || flowId === "new") {
      showAlert(
        "Important",
        "Please save your work so far before working with custom properties."
      );
      return;
    }

    const flowProps = await loadFlowProps(
      flowId ? flowId : "",
      useStore.getState().useLocalStorage
    );
    setInitialCustomProperties(flowProps || []);
    setShowCustomPropertiesModal(true);
  };

  const handleSaveCustomProperties = async (
    customProperties: CustomProperty[]
  ) => {
    if (!flowId || flowId === "new") {
      showAlert(
        "Important",
        "Please save your map before saving custom properties."
      );
      return;
    }

    const result = await saveFlowProps(
      flowId ? flowId : "",
      customProperties,
      useStore.getState().useLocalStorage
    );
    if (result.success) {
      setInitialCustomProperties(customProperties);

      const nodes = useStore.getState().nodes;
      const updatedNodes = nodes.map((node) => {
        const updatedCustomProperties = customProperties.map((customProp) => {
          const existingProp = node.data.customProperties?.find(
            (prop: CustomProperty) => prop.name === customProp.name
          );
          if (existingProp) {
            // Update type and visible properties
            return {
              ...existingProp,
              type: customProp.type,
              visible: customProp.visible,
              ...(customProp.type === "calculated" && {
                value: customProp.value,
              }),
            };
          } else {
            // Add new property definition with empty value (except calculated fields need formula)
            return {
              name: customProp.name,
              type: customProp.type,
              value:
                customProp.type === "calculated"
                  ? customProp.value
                  : customProp.type === "boolean"
                  ? false
                  : "",
              visible: customProp.visible,
            };
          }
        });

        // Remove properties not in customProperties
        const filteredCustomProperties = updatedCustomProperties.filter(
          (prop) =>
            customProperties.some((customProp) => customProp.name === prop.name)
        );

        return {
          ...node,
          data: {
            ...node.data,
            customProperties: filteredCustomProperties,
            values: filteredCustomProperties
              .map((prop: CustomProperty) => {
                if (prop.visible) {
                  if (prop.type === "calculated") {
                    const currency = useStore.getState().currency || "EUR";
                    const value = getCalculatedValue(
                      prop,
                      filteredCustomProperties,
                      currency
                    );
                    return value || { title: prop.name, value: "0" };
                  } else {
                    // Only include values that are actually set (non-empty)
                    const hasValue =
                      prop.type === "boolean"
                        ? true // boolean always has a value
                        : prop.value !== "" &&
                          prop.value !== null &&
                          prop.value !== undefined;

                    if (hasValue) {
                      let displayValue = prop.value;
                      // Add currency suffix for currency type properties
                      if (prop.type === "currency") {
                        const currency = useStore.getState().currency || "EUR";
                        displayValue = `${prop.value} ${currency}`;
                      }
                      return { title: prop.name, value: displayValue };
                    } else {
                      return { title: prop.name, value: "0" };
                    }
                  }
                } else {
                  return { title: prop.name, value: "0" };
                }
              })
              .filter((value: values) => value.value !== "0") as values[] | [],
          },
        };
      });

      useStore.getState().setNodes(updatedNodes);
    } else {
      showAlert("Error", "Failed to save custom properties.");
    }
  };

  const doPdfImport = async () => {
    showAlert("Import", "PDF import is not available yet.");
  };

  const doMindManagerImport = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".opml";
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        setLoadingMessage(["Importing", "OPML", "file..."]);
        setLoading(true);
        try {
          const result = await handleImportMindManager(file);
          if (result.success && result.nodes && result.edges) {
            onSetNodes(result.nodes);
            onSetEdges(result.edges);
            showAlert("Import", result.description);
          } else {
            showAlert("Import Error", result.description);
          }
        } finally {
          setLoading(false);
          setLoadingMessage(["Bringing", "your topic", "to life..."]);
        }
      }
    };
    input.click();
  };

  const doPdfExport = async () => {
    setLoadingMessage(["Capturing", "screenshot and", "creating PDF..."]);
    setLoading(true);
    try {
      const result: ResponseType = await handleExportToPDF(
        useStore.getState().nodes,
        useStore.getState().edges,
        captureScreenshot
      );
      if (result.success) {
        showAlert("Export", result.description);
      } else {
        showAlert("Export Error", result.description);
      }
    } finally {
      setLoading(false);
      setLoadingMessage(["Bringing", "your topic", "to life..."]);
    }
  };

  const doExcelExport = async () => {
    const result: ResponseType = await handleExportToExcel(
      useStore.getState().nodes,
      useStore.getState().edges
    );
    if (result.success) {
      showAlert("Export", result.description);
    } else {
      showAlert("Export Error", result.description);
    }
  };

  const doWordExport = async () => {
    setLoadingMessage(["Capturing", "screenshot and", "creating document..."]);
    setLoading(true);
    try {
      const result: ResponseType = await handleExportToWord(
        useStore.getState().nodes,
        useStore.getState().edges,
        true,
        captureScreenshot
      );
      if (result.success) {
        showAlert("Export", result.description);
      } else {
        showAlert("Export Error", result.description);
      }
    } finally {
      setLoading(false);
      setLoadingMessage(["Bringing", "your topic", "to life..."]);
    }
  };

  const handleSetIdeationMode = (value: boolean) => {
    setIdeationMode(value);
  };

  const handleSetMS365Mode = (value: boolean) => {
    if (typeof setMS365Mode === "function") {
      setMS365Mode(value);
    } else {
      console.warn("setMS365Mode is not a function", setMS365Mode);
    }
  };

  const handleSetAIButton = (value: boolean) => {
    setAIMode(value);
  };

  const handleSetDetailsButton = (value: boolean) => {
    setDetailsMode(value);
  };

  const handleToggleShowValues = () => {
    const newValue = !showValues;
    setShowValues(newValue);
    useStore.getState().setShowValues(newValue);
    if (!newValue) {
      setDoRollup(false);
      useStore.getState().setDoRollup(false);
    }
  };

  const handleToggleRollup = () => {
    const newValue = !doRollup;
    setDoRollup(newValue);
    useStore.getState().setDoRollup(newValue);
  };

  const endMenuItems: ExtendedMenuItem[] = [
    {
      template: () => {
        const incomingCount = incomingDataCount;
        return (
          <Button
            onClick={() => setShowIncomingDataDialog(true)}
            className="p-button-text p-button-plain"
            tooltip="Incoming Data"
            tooltipOptions={{ position: "bottom" }}
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: incomingCount > 0 ? "#14b8a6" : "#6c757d",
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <i className="pi pi-inbox" style={{ fontSize: "1.25rem" }} />
            {incomingCount > 0 && (
              <Badge
                value={incomingCount}
                severity="danger"
                style={{
                  position: "absolute",
                  top: "2px",
                  right: "4px",
                  minWidth: "1.25rem",
                  height: "1.25rem",
                  lineHeight: "1.25rem",
                }}
              />
            )}
          </Button>
        );
      },
    },
    {
      template: () => (
        <Button
          onClick={() => {
            setTempFilterStatuses([...focusFilterStatuses]);
            setShowFocusFilterDialog(true);
          }}
          className="p-button-text p-button-plain"
          tooltip="Focus Filtering"
          tooltipOptions={{ position: "bottom" }}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            color: focusFilterStatuses.length > 0 ? "#14b8a6" : "#6c757d",
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="pi pi-filter" style={{ fontSize: "1.25rem" }} />
        </Button>
      ),
    },
  ];

  const end = (
    <div className="flex align-items-center gap-2 row">
      <IncomingDataButton
        flowId={flowId}
        onCountChange={setIncomingDataCount}
        externalVisible={showIncomingDataDialog}
        onVisibleChange={setShowIncomingDataDialog}
      />

      <IconField
        iconPosition="left"
        style={{
          marginRight: "0.5rem",
          marginTop: "0.2rem",
          marginBottom: "-0.2rem",
          fontSize: "14px",
        }}
      >
        {hasSearchHits ? (
          <InputIcon
            className="pi pi-eye"
            style={{
              position: "absolute",
              left: "190px",
              top: "25px",
              cursor: "pointer",
            }}
            onClick={() => navigateSelected("forward")}
          ></InputIcon>
        ) : (
          <></>
        )}

        <InputText
          placeholder="Search..."
          value={searchText}
          onChange={handleSearchTextChange}
          onKeyDown={handleKeyPress}
          type="text"
          className="search-input"
          style={{
            fontSize: "14px",
            height: "24px",
            width: "200px",
            marginTop: "4px",
          }}
        />
      </IconField>

      <Button
        icon="pi pi-search"
        onClick={handleSearchSubmit}
        className="p-button-text p-button-plain"
        style={{
          width: "2.5rem",
          height: "2.5rem",
          color: "#6c757d",
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "0.2rem",
        }}
      />
    </div>
  );

  const itemRenderer = (item: any) => (
    <a className="flex align-items-center p-menuitem-link">
      <span className={item.icon} style={{ minWidth: "25px" }} />
      <span className="mx-2">{item.label}</span>
      {item.badge && <Badge className="ml-auto" value={item.badge} />}
      {item.shortcut && (
        <span
          style={{ position: "absolute", right: "20px" }}
          className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1"
        >
          {item.shortcut}
        </span>
      )}
    </a>
  );

  const iconRenderer = (item: any) => (
    <a className="flex align-items-center p-menuitem-link">
      <span
        className={item.icon}
        style={{ minWidth: "15px", fontSize: "20px", color: "#4C566B" }}
      />
      {item.badge && <Badge className="ml-auto" value={item.badge} />}
    </a>
  );

  const toolbarItems = [
    {
      label: "Update",
      icon: "pi pi-refresh",
    },
    {
      label: "Delete",
      icon: "pi pi-times",
    },
  ];

  // const endContent = (
  //   <React.Fragment>
  //     <SplitButton
  //       label="Save"
  //       model={toolbarItems}
  //       icon="pi pi-check"
  //     ></SplitButton>
  //   </React.Fragment>
  // );

  const items: ExtendedMenuItem[] = [
    {
      label: "Home",
      icon: "pi pi-home",
      command: () => {
        handleBackClick();
      },
    },
    {
      label: "Save",
      icon: "pi pi-database",
      command: () => {
        onSave();
      },
    },
    {
      label: "Data",
      icon: "pi pi-database",
      items: [
        {
          label: "Import From",
          icon: "pi pi-file-import",
          items: [
            {
              label: "Map File",
              icon: "pi pi-sitemap",
              command: () => {
                onImport(null);
              },
            },
            { separator: true },
            {
              label: "Microsoft 365",
              icon: "microsoft",
              template: (item) => (
                <a className="flex align-items-center p-menuitem-link">
                  <span style={{ minWidth: "25px", fontSize: "16px" }}>
                    <BsMicrosoft />
                  </span>
                  <span className="mx-2">{item.label}</span>
                </a>
              ),
              command: () => {
                handleSetMS365Mode(!ms365Mode);
              },
            },
            { separator: true },
            {
              label: "Word",
              icon: "pi pi-file-word",
              disabled: false, // userPlan?.includes("free"),
              command: () => {
                onImportWord();
              },
            },
            {
              label: "Excel", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file-excel",
              disabled: true,
              command: () => {
                showAlert("Import", "Excel import is not available yet.");
              },
            },
            {
              label: "PDF", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file-pdf",
              disabled: true, // userPlan?.includes("free"),
              command: () => {
                showAlert("Import", "PDF import is not available yet.");
              },
            },
            { separator: true },
            {
              label: "OPML file", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file",
              disabled: false, // userPlan?.includes("free"),
              command: () => {
                doMindManagerImport();
              },
            },
          ],
        },
        {
          label: "Export To",
          icon: "pi pi-file-export",
          items: [
            {
              label: "Map File",
              icon: "pi pi-sitemap",
              command: () => {
                onExport();
              },
            },
            {
              label: "Image",
              icon: "pi pi-image",
              command: () => {
                onExportToPng();
              },
            },
            {
              label: "Clipboard",
              icon: "pi pi-clipboard",
              command: () => {
                onExportToClipboard();
              },
            },
            { separator: true },
            {
              label: "Word", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file-word",
              disabled: false, //userPlan?.includes("free"),
              template: itemRenderer,
              command: () => {
                doWordExport();
              },
            },
            {
              label: "Excel", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file-excel",
              disabled: false, //userPlan?.includes("free"),
              template: itemRenderer,
              command: () => {
                doExcelExport();
              },
            },
            {
              label: "PDF", // + (userPlan?.includes("free") ? "💎" : ""),
              icon: "pi pi-file-pdf",
              disabled: false, //userPlan?.includes("free"),
              template: itemRenderer,
              command: () => {
                doPdfExport();
              },
            },
          ],
        },
        { separator: true },
        {
          label: "Save as Template",
          icon: "pi pi-file-check",
          command: () => {
            onSaveAsTemplate();
          },
        },
        { separator: true },
        {
          label: "Share...", // + (userPlan?.includes("free") ? "💎" : ""),
          icon: "pi pi-share-alt",
          command: () => {
            handleShare();
          },
        },
      ],
    },
    {
      label: "Edit",
      icon: "pi pi-pencil",
      items: [
        {
          label: "Undo",
          icon: "pi pi-undo",
          command: () => {
            onUndo();
          },
        },
        {
          label: "Redo",
          icon: "pi pi-replay",
          command: () => {
            onRedo();
          },
        },

        // { separator: true },
        // {
        //   label: "Layout",
        //   icon: "pi pi-th-large",
        //   items: [
        //     {
        //       label: "Radial",
        //       icon: "pi pi-bullseye",
        //       command: () => {
        //         onAutoCircle();
        //       },
        //     },
        //     {
        //       label: "Left",
        //       icon: "pi pi-arrow-circle-right",
        //       command: () => {
        //         onAutoTree("left");
        //       },
        //     },
        //     {
        //       label: "Down",
        //       icon: "pi pi-arrow-circle-down",
        //       command: () => {
        //         onAutoTree("down");
        //       },
        //     },
        //   ],
        // },
        { separator: true },
        // {
        //   label: "Priorities", // + (userPlan?.includes("free") ? "💎" : ""),
        //   icon: "pi pi-sort-numeric-down",
        //   className: focusValue === "priorities" ? "p-menuitem-active" : "",
        //   disabled: false, //userPlan?.includes("free"),
        //   template: itemRenderer,
        //   command: () => {
        //     handlePrioritiesMode();
        //   },
        // },
        {
          label: "Properties", // + (userPlan?.includes("free") ? "💎" : ""),
          icon: "pi pi-code",
          className: focusValue === "properties" ? "p-menuitem-active" : "",
          disabled: false, //userPlan?.includes("free"),
          // template: itemRenderer,
          command: () => {
            handleLoadCustomProperties();
          },
        },
        { separator: true },
        {
          label: "Shortcuts",
          icon: "pi pi-bolt",
          items: [
            {
              label: "Layout",
              icon: "pi pi-circle",
              command: handleLayoutChildren,
              shortcut: navigator.platform.includes("Mac") ? "⌘L" : "Ctrl+L",
              template: itemRenderer,
            },
            {
              label: "Layout All",
              icon: "pi pi-sitemap",
              command: handleLayoutAllBranches,
              shortcut: navigator.platform.includes("Mac")
                ? "⌘⇧L"
                : "Ctrl+Shift+L",
              template: itemRenderer,
            },
            {
              label: "Radial Layout",
              icon: "pi pi-sun",
              command: handleLayoutRadial,
              shortcut: navigator.platform.includes("Mac")
                ? "⌘⇧R"
                : "Ctrl+Shift+R",
              template: itemRenderer,
            },
            {
              label: "More Space",
              icon: "pi pi-plus",
              command: handleIncreaseSpacing,
              shortcut: navigator.platform.includes("Mac") ? "⌘+" : "Ctrl++",
              template: itemRenderer,
            },
            {
              label: "Less Space",
              icon: "pi pi-minus",
              command: handleDecreaseSpacing,
              shortcut: navigator.platform.includes("Mac") ? "⌘-" : "Ctrl+-",
              template: itemRenderer,
            },
            {
              label: "More Space (Global)",
              icon: "pi pi-plus-circle",
              command: handleIncreaseGlobalSpacing,
              shortcut: navigator.platform.includes("Mac")
                ? "⌘⇧+"
                : "Ctrl+Shift++",
              template: itemRenderer,
            },
            {
              label: "Less Space (Global)",
              icon: "pi pi-minus-circle",
              command: handleDecreaseGlobalSpacing,
              shortcut: navigator.platform.includes("Mac")
                ? "⌘⇧-"
                : "Ctrl+Shift+-",
              template: itemRenderer,
            },
            { separator: true },
            {
              label: "How it works...",
              icon: "pi pi-question-circle",
              command: () => setShowTips(1),
            },
          ],
        },
      ],
    },
    {
      label: "View",
      icon: "pi pi-eye",
      items: [
        // {
        //   label: "Current View",
        //   icon: "pi pi-eye",
        //   items: [
        //     {
        //       label: viewTypes.FocusMap.label,
        //       icon: "pi pi-circle",
        //       template: itemRenderer,
        //       command: () => {
        //         setCurrentView(viewTypes.FocusMap);
        //       },
        //     },
        //     {
        //       label: viewTypes.TimeLine.label,
        //       icon: "pi pi-clock",
        //       disabled: false,
        //       template: itemRenderer,
        //       command: () => {
        //         setCurrentView(viewTypes.TimeLine);
        //       },
        //     },
        //     {
        //       label: viewTypes.Grid.label,
        //       icon: "pi pi-table",
        //       disabled: false,
        //       template: itemRenderer,
        //       command: () => {
        //         setCurrentView(viewTypes.Grid);
        //       },
        //     },
        //     {
        //       label: viewTypes.WorldMap.label,
        //       icon: "pi pi-map",
        //       disabled: false,
        //       template: itemRenderer,
        //       command: () => {
        //         setCurrentView(viewTypes.WorldMap);
        //       },
        //     },
        //     {
        //       label: viewTypes.Wheel.label,
        //       icon: "pi pi-circle",
        //       disabled: false,
        //       template: itemRenderer,
        //       command: () => {
        //         setCurrentView(viewTypes.Wheel);
        //       },
        //     },
        //   ],
        // },
        // { separator: true },
        {
          label: "Snap to Grid",
          icon: "pi pi-table",
          command: () => {
            onToggleSnapToGrid();
          },
          template: (item: any) => (
            <a
              className="flex align-items-center p-menuitem-link"
              style={{ position: "relative" }}
            >
              <span className={item.icon} style={{ minWidth: "25px" }} />
              <span style={{ marginLeft: "-6px" }}>{item.label}</span>
              {snapToGrid && (
                <span
                  className="pi pi-check"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#167d3cff",
                  }}
                />
              )}
            </a>
          ),
        },
        {
          label: "Show MiniMap",
          icon: "pi pi-map",
          command: () => {
            onToggleShowMiniMap();
          },
          template: (item: any) => (
            <a
              className="flex align-items-center p-menuitem-link"
              style={{ position: "relative" }}
            >
              <span className={item.icon} style={{ minWidth: "25px" }} />
              <span style={{ marginLeft: "-6px" }}>{item.label}</span>
              {showMiniMap && (
                <span
                  className="pi pi-check"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#167d3cff",
                  }}
                />
              )}
            </a>
          ),
        },
        { separator: true },
        {
          label: "Show Values",
          icon: "pi pi-hashtag",
          command: () => {
            handleToggleShowValues();
          },
          template: (item: any) => (
            <a
              className="flex align-items-center p-menuitem-link"
              style={{ position: "relative" }}
            >
              <span style={{ minWidth: "25px" }}>
                <TbNumbers />
              </span>
              <span style={{ marginLeft: "-6px" }}>{item.label}</span>
              {showValues && (
                <span
                  className="pi pi-check"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#167d3cff",
                  }}
                />
              )}
            </a>
          ),
        },
        {
          label: "Rollup Values",
          icon: "pi pi-calculator",
          command: () => {
            if (showValues) {
              handleToggleRollup();
            }
          },
          template: (item: any) => (
            <a
              className="flex align-items-center p-menuitem-link"
              style={{ position: "relative", opacity: !showValues ? 0.4 : 1 }}
            >
              <span style={{ minWidth: "25px" }}>
                <TbCalculator />
              </span>
              <span style={{ marginLeft: "-6px" }}>{item.label}</span>
              {doRollup && showValues && (
                <span
                  className="pi pi-check"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#167d3cff",
                  }}
                />
              )}
            </a>
          ),
        },
        { separator: true },
        {
          label: "Focus Filtering",
          icon: "pi pi-filter",
          command: () => {
            setTempFilterStatuses([...focusFilterStatuses]);
            setShowFocusFilterDialog(true);
          },
        },
        { separator: true },
        {
          label: "Presentation", // + (userPlan?.includes("free") ? "💎" : ""),
          icon: "pi pi-desktop",
          items: [
            {
              label: "Set Order",
              icon: "pi pi-sort-numeric-down",
              disabled: false, //userPlan?.includes("free"),
              template: itemRenderer,
              command: () => {
                handlePresentationOrderMode();
              },
            },
            {
              label: "Start",
              icon: "pi pi-play-circle",
              // shortcut: "F5",
              disabled: false, //userPlan?.includes("free"),
              template: itemRenderer,
              command: () => {
                doPresent();
              },
            },
          ],
        },
      ],
    },
    // Temporarily hidden
    // {
    //   label: "Brainstorm",
    //   icon: "pi pi-sparkles",
    //   command: () => {
    //     setBrainstormVisible(true);
    //   },
    // },
    // {
    //   label: "Guide Me",
    //   icon: "pi pi-lightbulb",
    //   command: () => {
    //     setGuideVisible(!guideVisible);
    //   },
    // },
    ...endMenuItems,
    {
      template: () => (
        <Button
          onClick={() => setShowFeedbackModal(true)}
          className="p-button-text p-button-plain"
          tooltip="Send Feedback"
          tooltipOptions={{ position: "bottom" }}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            color: "#6c757d",
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="pi pi-thumbs-up" style={{ fontSize: "1.25rem" }} />
        </Button>
      ),
    },

    {
      template: () => (
        <Button
          onClick={() => handleHelp()}
          className="p-button-text p-button-plain"
          tooltip="Help"
          tooltipOptions={{ position: "bottom" }}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            color: "#6c757d",
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i
            className="pi pi-question-circle"
            style={{ fontSize: "1.25rem" }}
          />
        </Button>
      ),
    },
    {
      template: () => (
        <Button
          onClick={() => handleUserSettings()}
          className="p-button-text p-button-plain"
          tooltip="Settings"
          tooltipOptions={{ position: "bottom" }}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            color: "#6c757d",
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="pi pi-cog" style={{ fontSize: "1.25rem" }} />
        </Button>
      ),
    },
    {
      template: () => (
        <Button
          onClick={() => handleSetMS365Mode(!ms365Mode)}
          className="p-button-text p-button-plain"
          tooltip="Microsoft 365 Integration"
          tooltipOptions={{ position: "bottom" }}
          style={{
            width: "2.5rem",
            height: "2.5rem",
            color: ms365Mode ? "#14b8a6" : "#6c757d",
            backgroundColor: "transparent",
            border: "none",
            padding: 0,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BsMicrosoft style={{ fontSize: "1.25rem" }} />
        </Button>
      ),
    },
    {
      label: "Focus AI",
      icon: "pi pi-sparkles",
      command: () => {
        onToggleAIInput();
      },
    },
  ];

  return (
    <div className="menubar-container">
      {loading && (
        <div className="progress-overlay">
          <div className="progress-indicator"></div>
          <div className="working">
            {loadingMessage.map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        </div>
      )}

      <Menubar model={items} end={end} />

      {showShareModal && (
        <div className="modal">
          <div className="share-modal-content">
            <button
              className="modal-close-button"
              onClick={() => setShowShareModal(false)}
            >
              <IoClose size={18} />
            </button>
            <h2 className="modal-title">Invite Collaborator</h2>
            <label className="modal-label">Current collaborators:</label>
            <ul className="collaborator-list">
              {collaborators.map((collaborator, index) => (
                <li
                  key={index}
                  onClick={() => setSelectedCollaborator(collaborator)}
                  className={`collaborator-item ${
                    selectedCollaborator === collaborator ? "selected" : ""
                  }`}
                >
                  {collaborator}
                </li>
              ))}
            </ul>
            <button
              className="modal-button-left"
              onClick={handleRemoveCollaborator}
            >
              Remove
            </button>
            <div className="row add-area">
              <label className="modal-label">Email:&nbsp;</label>
              <input
                type="email"
                placeholder="enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="modal-input"
                disabled={isAddingCollaborator}
              />
              <button
                className="modal-button add"
                onClick={handleAddCollaborator}
                disabled={isAddingCollaborator}
                style={{ minWidth: "60px" }}
              >
                {isAddingCollaborator ? (
                  <ProgressSpinner
                    style={{ width: "16px", height: "16px" }}
                    strokeWidth="6"
                  />
                ) : (
                  "Add"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showFeedbackModal && (
        <FeedbackForm
          feedback={feedback}
          setFeedback={setFeedback}
          setShowFeedbackModal={setShowFeedbackModal}
          handleFeedbackSubmit={handleFeedbackSubmit}
        />
      )}
      <HelpModal showHelpModal={showHelpModal} onClose={handleCloseHelpModal} />

      <ProModal
        showProModal={showProModal}
        onClose={handleCloseProModal}
        onButtonClick={handleProButtonClick}
      />

      {alertMessage && (
        <CustomAlert
          title={alertTitle}
          message={alertMessage}
          htmlMessage={null}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <CustomPropertiesModal
        isOpen={showCustomPropertiesModal}
        inputOnly={false}
        onClose={() => setShowCustomPropertiesModal(false)}
        onSave={handleSaveCustomProperties}
        initialProperties={initialCustomProperties}
      />
      <Dialog
        header="Microsoft 365"
        modal={true}
        visible={ms365Mode}
        closable
        style={{
          width: "460px",
        }}
        onHide={() => {
          setMS365Mode(false);
          setSearchText("");
        }}
      >
        <div
          style={{
            position: "relative",
            marginTop: "0rem",
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            marginBottom: "1rem",
          }}
        >
          <label
            style={{ marginRight: "7px", marginTop: "5px", minWidth: "50px" }}
          >
            Search:&nbsp;&nbsp;
          </label>
          <InputText
            value={searchText}
            onChange={handleSearchTextChange}
            onKeyDown={handleKeyPress}
            placeholder="Enter search term"
            style={{ flex: 1, height: "1.3rem", marginTop: "13px" }}
          />
          <Button
            icon="pi pi-search"
            onClick={handleSearchSubmit}
            className="p-button-text p-button-plain"
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: "#6c757d",
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              marginTop: "13px",
              marginLeft: "0.5rem",
              boxShadow: "none",
            }}
          />
        </div>
        <div
          style={{
            position: "relative",
            marginTop: "1rem",
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <label style={{ marginRight: "7px" }}>Max results per type:</label>
          <InputText
            value={maxResults.toString()}
            onChange={(e) => setMaxResults(Number(e.target.value))}
            style={{ width: "30px", marginLeft: "10px", textAlign: "center" }}
          />
          <Slider
            value={maxResults}
            onChange={(e) => setMaxResults(Number(e.value))}
            min={1}
            max={100}
            style={{ width: "165px", marginLeft: "20px", marginTop: "-5px" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            marginTop: "1rem",
          }}
        >
          <span style={{ width: "40px" }}>From:</span>
          <Calendar
            id="ms365FromDate"
            showIcon
            value={ms365FromDate}
            onChange={(e) => changeMS365FromDate(e.value)}
            placeholder="Start date"
            style={{
              width: "150px",
              height: "32px",
              verticalAlign: "middle",
            }}
            inputStyle={{ height: "14px" }}
            className="calendar-custom-icon"
          />
          <span>To:</span>
          <Calendar
            id="ms365ToDate"
            showIcon
            value={ms365ToDate}
            onChange={(e) => changeMS365ToDate(e.value)}
            placeholder="End date"
            style={{
              width: "150px",
              height: "32px",
              verticalAlign: "middle",
            }}
            inputStyle={{ height: "14px" }}
            className="calendar-custom-icon"
          />
        </div>
        <div
          style={{
            position: "relative",
            marginTop: "1rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <label style={{ marginRight: "7px" }}>Types:</label>
          <MultiSelect
            value={selectedFileTypes}
            options={[
              { label: "Email", value: "email" },
              { label: "Event", value: "event" },
              { label: "Chat", value: "chat" },
              { label: "Word Document", value: "docx" },
              { label: "Excel Spreadsheet", value: "xlsx" },
              { label: "PowerPoint Presentation", value: "pptx" },
              { label: "PDF", value: "pdf" },
              { label: "OneNote", value: "onenote" },
              { label: "Teams Message", value: "teams" },
              { label: "SharePoint File", value: "sharepoint" },
              { label: "Outlook Attachment", value: "attachment" },
              { label: "Contact", value: "contact" },
              { label: "Task (To Do)", value: "todo" },
              { label: "Notes", value: "notes" },
              { label: "Journal", value: "journal" },
            ]}
            onChange={(e) => setSelectedFileTypes(e.value)}
            placeholder="Select file types"
            display="comma"
            maxSelectedLabels={3}
            style={{ width: "100%" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: "1rem",
          }}
        >
          <label style={{ marginRight: "7px" }}>Output:</label>
          <span>Pick List</span>
          {/* <SelectButton
            value={ms365OutputMode}
            onChange={(e) => setMS365OutputMode(e.value)}
            options={[
              { label: "Pick List", value: "picklist" },
              { label: "Canvas", value: "canvas" },
            ]}
            className="ms365-output"
          /> */}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: "1rem",
            opacity: 0.5,
          }}
        >
          <InputSwitch
            checked={useCopilot}
            onChange={(e) => setUseCopilot(e.value)}
            id="useCopilot"
            disabled={true}
          />
          <label htmlFor="useCopilot" style={{ color: "#999" }}>
            Ask Copilot (Beta)
          </label>
        </div>
      </Dialog>

      <Dialog
        header="Microsoft 365 - Pick Items"
        visible={showMS365PickListDialog}
        style={{ width: "70vw", maxHeight: "80vh" }}
        onHide={() => {
          setShowMS365PickListDialog(false);
          setMS365PickListSearchText("");
        }}
        footer={
          <div>
            <Button
              label="Close"
              icon="pi pi-times"
              onClick={() => {
                setShowMS365PickListDialog(false);
                setMS365PickListSearchText("");
              }}
              className="p-button-text"
            />
          </div>
        }
      >
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <InputText
            value={ms365PickListSearchText}
            onChange={(e) => setMS365PickListSearchText(e.target.value)}
            placeholder="Search in results..."
            style={{ flex: 1 }}
          />
          <Button
            icon="pi pi-search"
            onClick={() => {
              // Trigger re-filter by updating state (search already filters reactively)
            }}
            className="p-button-text p-button-plain"
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: "#6c757d",
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              boxShadow: "none",
            }}
          />
        </div>
        <div style={{ maxHeight: "calc(80vh - 250px)", overflowY: "auto" }}>
          {ms365PickListData
            .filter((item) =>
              ms365PickListSearchText
                ? item.title
                    .toLowerCase()
                    .includes(ms365PickListSearchText.toLowerCase()) ||
                  (item.summary &&
                    item.summary
                      .toLowerCase()
                      .includes(ms365PickListSearchText.toLowerCase()))
                : true
            )
            .map((item) => (
              <div
                key={item.id}
                style={{
                  padding: "1rem",
                  borderBottom: "1px solid #e0e0e0",
                  cursor: "pointer",
                }}
                // onClick={() => {
                //   // Add item to map
                //   const event = new CustomEvent("addMS365ItemToMap", {
                //     detail: item,
                //   });
                //   window.dispatchEvent(event);
                // }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <img
                    src={`https://static2.sharepointonline.com/files/fabric/assets/item-types/96/${
                      item.type === "event"
                        ? "calendar"
                        : item.type === "email"
                        ? "email"
                        : item.type === "chat"
                        ? "chat"
                        : "genericfile"
                    }.png`}
                    alt={item.type}
                    style={{ width: "32px", height: "32px" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "bold" }}>{item.title}</div>
                    {item.summary && (
                      <div style={{ fontSize: "0.9rem", color: "#666" }}>
                        {item.summary.substring(0, 100)}
                        {item.summary.length > 100 ? "..." : ""}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#999",
                        marginTop: "0.25rem",
                      }}
                    >
                      {new Date(item.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    icon={
                      addedMS365Items.has(item.id)
                        ? "pi pi-check"
                        : "pi pi-plus"
                    }
                    className="p-button-rounded p-button-text add-msitem-button"
                    style={
                      addedMS365Items.has(item.id)
                        ? { color: "#22c55e" }
                        : undefined
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const event = new CustomEvent("addMS365ItemToMap", {
                        detail: item,
                      });
                      window.dispatchEvent(event);
                      setAddedMS365Items((prev) => new Set(prev).add(item.id));
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </Dialog>

      <Dialog
        header="Focus Filtering"
        visible={showFocusFilterDialog}
        style={{ width: "400px" }}
        onHide={() => setShowFocusFilterDialog(false)}
        footer={
          <div>
            <Button
              label="Apply"
              icon="pi pi-check"
              onClick={handleApplyFocusFilter}
              autoFocus
            />
            <Button
              label="Clear"
              icon="pi pi-times"
              onClick={clearFocusFilter}
              className="p-button-text"
            />
            <Button
              label="Close"
              icon="pi pi-sign-out"
              onClick={() => setShowFocusFilterDialog(false)}
              className="p-button-text"
            />
          </div>
        }
      >
        <div style={{ padding: "1rem 0" }}>
          <p style={{ marginBottom: "1rem" }}>
            Select task statuses to focus on:
          </p>
          {taskStatusOptions.map((option) => (
            <div
              key={option.value}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "0.75rem",
              }}
            >
              <Checkbox
                inputId={option.value}
                value={option.value}
                onChange={() => handleFocusFilterChange(option.value)}
                checked={tempFilterStatuses.includes(option.value)}
              />
              <label
                htmlFor={option.value}
                style={{ marginLeft: "0.5rem", cursor: "pointer" }}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  );
};

export default MenuBar;
